import React, { useEffect } from 'react';
import { Button, Drawer, Form, Input, Select, Space } from 'antd';
import { useTheme } from '../../context/ThemeContext';
import {
  CODE_PREFIX_PATTERN,
  CREATOR_STATUS_OPTIONS,
  EDIT_LIMITED_FIELDS_NOTICE,
  FIELD_LIMITS,
} from './constants';
import { InlineNotice } from './Styles';
import { useUpdateCreator } from './hooks/useCreatorMutations';
import type { Creator, CreatorStatus, UpdateCreatorPayload } from './types';

interface EditFormValues {
  status: CreatorStatus;
  codePrefix: string;
}

interface CreatorEditDrawerProps {
  open: boolean;
  creator: Creator | null;
  onClose: () => void;
}

/**
 * §2.4 — edit a creator.
 *
 * Only `status` and `codePrefix` are editable. `email`, `name`, `handle` and
 * `password` have no update endpoint today (docs §1.3), so rather than silently
 * omitting them the drawer says why they're absent.
 *
 * Both fields are independent server-side, so only the ones that actually changed
 * are sent — submitting an unchanged form is a no-op rather than a redundant write.
 */
const CreatorEditDrawer: React.FC<CreatorEditDrawerProps> = ({ open, creator, onClose }) => {
  const [form] = Form.useForm<EditFormValues>();
  const { isDarkMode } = useTheme();
  const { mutate: updateCreator, isPending } = useUpdateCreator();

  useEffect(() => {
    if (open && creator) {
      form.setFieldsValue({
        status: creator.status,
        codePrefix: creator.codePrefix,
      });
    }
  }, [open, creator, form]);

  const handleSubmit = (values: EditFormValues) => {
    if (!creator) return;

    const payload: UpdateCreatorPayload = {};
    if (values.status !== creator.status) payload.status = values.status;
    if (values.codePrefix.trim() !== creator.codePrefix) {
      payload.codePrefix = values.codePrefix.trim();
    }

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    updateCreator({ id: creator.id, payload }, { onSuccess: onClose });
  };

  return (
    <Drawer
      title={creator ? `Edit ${creator.name}` : 'Edit Creator'}
      placement="right"
      onClose={onClose}
      open={open}
      size={600}
      destroyOnClose
      styles={{
        body: { background: isDarkMode ? '#1a202c' : '#ffffff' },
        header: {
          background: isDarkMode ? '#1a202c' : '#ffffff',
          borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
        },
      }}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="primary" loading={isPending} onClick={() => form.submit()}>
            Save Changes
          </Button>
        </Space>
      }
    >
      <InlineNotice>{EDIT_LIMITED_FIELDS_NOTICE}</InlineNotice>

      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={isPending}>
        <Form.Item
          label="Status"
          name="status"
          extra="Pausing or terminating takes effect on the creator's very next portal request."
          rules={[{ required: true, message: 'Please select a status' }]}
        >
          <Select options={CREATOR_STATUS_OPTIONS} />
        </Form.Item>

        <Form.Item
          label="Code Prefix"
          name="codePrefix"
          extra="Must be unique within your merchant account."
          rules={[
            { required: true, message: 'Please enter a code prefix' },
            {
              max: FIELD_LIMITS.CODE_PREFIX_MAX,
              message: `At most ${FIELD_LIMITS.CODE_PREFIX_MAX} characters`,
            },
            { pattern: CODE_PREFIX_PATTERN, message: 'Letters and numbers only' },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CreatorEditDrawer;
